// apps/spa/src/hooks/useTimeFormat.ts
// User preference: 12-hour vs 24-hour display for absolute timestamps + schedule
// summaries. Per-device (localStorage) — cross-device sync would need a D1 user_prefs
// table; not worth the complexity for one display preference.
//
// Cross-component sync inside the same tab is via a CustomEvent on `document`
// (matches the existing `session-expired` pattern in lib/api.ts — natural choice
// since useState in two separate hook instances doesn't auto-sync).
// Cross-tab sync is via the browser's `storage` event.

import { useEffect, useState, useCallback } from 'react';

export type TimeFormat = '12h' | '24h';

const STORAGE_KEY = 'guardian:timeFormat';
const CHANGE_EVENT = 'guardian:time-format-changed';

function readStored(): TimeFormat {
  try {
    return localStorage.getItem(STORAGE_KEY) === '24h' ? '24h' : '12h';
  } catch {
    // localStorage can throw in some privacy modes — fall back to default.
    return '12h';
  }
}

export function useTimeFormat(): {
  format: TimeFormat;
  hour12: boolean;
  toggle: () => void;
} {
  const [format, setFormat] = useState<TimeFormat>(readStored);

  useEffect(() => {
    function onChange(e: Event) {
      const next = (e as CustomEvent<TimeFormat>).detail;
      if (next === '12h' || next === '24h') setFormat(next);
    }
    function onStorage(e: StorageEvent) {
      if (e.key === STORAGE_KEY) setFormat(e.newValue === '24h' ? '24h' : '12h');
    }
    document.addEventListener(CHANGE_EVENT, onChange);
    window.addEventListener('storage', onStorage);
    return () => {
      document.removeEventListener(CHANGE_EVENT, onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const toggle = useCallback(() => {
    const next: TimeFormat = readStored() === '12h' ? '24h' : '12h';
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Privacy-mode fallback: state still flips in-memory via the dispatched event.
    }
    document.dispatchEvent(new CustomEvent<TimeFormat>(CHANGE_EVENT, { detail: next }));
  }, []);

  return { format, hour12: format === '12h', toggle };
}
